import { Component } from '@theme/component';
import { onAnimationEnd } from '@theme/utilities';
import { ThemeEvents, CartUpdateEvent } from '@theme/events';

/**
 * A custom element that displays a cart icon.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} cartBubble - The cart bubble element.
 * @property {HTMLElement} cartBubbleText - The cart bubble text element.
 *
 * @extends {Component<Refs>}
 */
class CartIcon extends Component {
  requiredRefs = ['cartBubble', 'cartBubbleText'];

  /**
   * The element that holds the visible count. The cart bubble also contains a visually hidden
   * announcement for screen readers, so the count must never be read from or written to the
   * wrapper element itself.
   * @type {HTMLElement | null}
   */
  get countElement() {
    return this.refs.cartBubbleText.querySelector('.cart-bubble__text-count');
  }

  /**
   * The element that announces the count to screen readers.
   * @type {HTMLElement | null}
   */
  get announcementElement() {
    return this.refs.cartBubbleText.querySelector('.visually-hidden');
  }

  /** @type {number} */
  get currentCartCount() {
    // The visible count is blank once it exceeds the render limit, but the announcement always
    // carries the full number, so read that first and only fall back to the visible count.
    const announced = parseInt(this.announcementElement?.textContent?.split(':').pop()?.trim() ?? '', 10);

    if (!Number.isNaN(announced)) return announced;

    const visible = parseInt(this.countElement?.textContent?.trim() ?? '', 10);

    return Number.isNaN(visible) ? 0 : visible;
  }

  set currentCartCount(value) {
    const countElement = this.countElement;

    if (countElement) countElement.textContent = value < 100 ? String(value) : '';

    const announcementElement = this.announcementElement;

    if (announcementElement) {
      const label = announcementElement.textContent?.split(':')[0]?.trim() ?? '';

      announcementElement.textContent = `${label}: ${value}`;
    }
  }

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
    this.ensureCartBubbleIsCorrect();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
  }

  /**
   * Handles the cart update event.
   * @param {CartUpdateEvent} event - The cart update event.
   */
  onCartUpdate = async (event) => {
    const itemCount = event.detail.data?.itemCount ?? 0;
    const comingFromProductForm = event.detail.data?.source === 'product-form-component';

    this.renderCartBubble(itemCount, comingFromProductForm);
  };

  /**
   * Renders the cart bubble.
   * @param {number} itemCount - The number of items in the cart.
   * @param {boolean} comingFromProductForm - Whether the cart update is coming from the product form.
   */
  renderCartBubble = async (itemCount, comingFromProductForm, animate = true) => {
    // If the cart update is coming from the product form, we add to the current cart count, otherwise we set the new cart count
    this.refs.cartBubble.classList.toggle('visually-hidden', itemCount === 0);
    this.refs.cartBubble.classList.toggle('cart-bubble--animating', itemCount > 0 && animate);
    this.currentCartCount = comingFromProductForm ? this.currentCartCount + itemCount : itemCount;

    this.classList.toggle('header-actions__cart-icon--has-cart', itemCount > 0);

    sessionStorage.setItem(
      'cart-count',
      JSON.stringify({
        value: String(this.currentCartCount),
        timestamp: Date.now(),
      })
    );

    if (!animate) return;
    await onAnimationEnd(this.refs.cartBubbleText);

    this.refs.cartBubble.classList.remove('cart-bubble--animating');
  };

  /**
   * Checks if the cart count is correct.
   */
  ensureCartBubbleIsCorrect = () => {
    const sessionStorageCount = sessionStorage.getItem('cart-count');
    if (sessionStorageCount === null) return;

    try {
      const { value, timestamp } = JSON.parse(sessionStorageCount);
      const count = parseInt(value, 10);

      if (Number.isNaN(count) || count < 0) return;
      if (count === this.currentCartCount) return;

      if (Date.now() - timestamp < 10000) {
        this.renderCartBubble(count, false, false);
      }
    } catch (_) {
      // no-op
    }
  };
}

if (!customElements.get('cart-icon')) {
  customElements.define('cart-icon', CartIcon);
}
